<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import PeriodPopup from '#domain/core/filter/components/PeriodPopup.vue';
import { formatCustomDateToLocaleFR } from '#i18n';
import { useUserStore } from '#stores/user.store';
import {
  formatNielsenPeriodToReadableStr,
  nielsenPeriodStrToNielsenPeriod,
  type CarrefourReferentialIntervalType,
  type ComparisonPeriodType,
  type CustomDateType,
  type DatePeriodIntervalType,
  type NielsenPeriodIntervalType,
  type UserScope
} from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { AvailablePeriodType } from './periodSelector/types/availablePeriod.type';
import { type ReferentialPeriodType } from './periodSelector/types/referentialPeriod.type';

const {
  referentialPeriod,
  hasComparisonPeriod = false,
  onlyCarrefour = false
} = defineProps<{
  referentialPeriod: ReferentialPeriodType;
  carrefourReferentialIntervals: keyof CarrefourReferentialIntervalType;
  hasComparisonPeriod?: boolean;
  onlyCarrefour?: boolean;
}>();

const { t } = useLanguage();

const userStore = useUserStore();

const {
  taxonomy: { taxonomy },
  period: { selectedPeriod, comparisonPeriod, updatePeriod, referentialIntervals },
  isDefaultPerimeter
} = useKpisPerimeter();

const { scope } = storeToRefs(userStore);
const showPeriodSelectorPopUp = ref(false);

const intervalPeriodLabels = computed(() => {
  if (selectedPeriod.value.period === 'nielsen') {
    const { start, end } = (selectedPeriod.value as NielsenPeriodIntervalType).periodInterval;
    return {
      start: formatNielsenPeriodToReadableStr(nielsenPeriodStrToNielsenPeriod(start)),
      end: formatNielsenPeriodToReadableStr(nielsenPeriodStrToNielsenPeriod(end))
    };
  }

  const { start, end } = (selectedPeriod.value as DatePeriodIntervalType).periodInterval;

  return {
    start: formatCustomDateToLocaleFR(start),
    end: formatCustomDateToLocaleFR(end)
  };
});

const comparisonPeriodLabels = computed(() => {
  if (comparisonPeriod.value.comparisonMode === 'previousYear' || !comparisonPeriod.value.periodInterval) {
    return null;
  }

  const { start, end } = comparisonPeriod.value.periodInterval;

  if (typeof start === 'string' && typeof end === 'string') {
    return {
      start: formatNielsenPeriodToReadableStr(nielsenPeriodStrToNielsenPeriod(start)),
      end: formatNielsenPeriodToReadableStr(nielsenPeriodStrToNielsenPeriod(end))
    };
  }

  return {
    start: formatCustomDateToLocaleFR(start as CustomDateType),
    end: formatCustomDateToLocaleFR(end as CustomDateType)
  };
});

const availablePeriods = computed((): AvailablePeriodType[] => {
  const userPeriods: UserScope['permissions']['periods'] = scope.value?.permissions?.periods ?? [];

  return userPeriods
    .filter((period) => !(onlyCarrefour && period === 'nielsen'))
    .map((period) => {
      let unavailableMessage: string | undefined = undefined;
      // Check if period available in current taxonomy
      if (!referentialPeriod[taxonomy.value].availablePeriod.periods.includes(period)) {
        unavailableMessage = referentialPeriod[taxonomy.value].availablePeriod.unavailableMessage;
      } else {
        const unavailablePeriods = referentialPeriod[taxonomy.value].unavailablePeriods;
        // Check if period is specifically unavailable (ie by a component)
        if (unavailablePeriods && unavailablePeriods.periods.includes(period)) {
          unavailableMessage = unavailablePeriods.unavailableMessage;
        }
      }

      return {
        period,
        unavailableMessage
      };
    });
});

watch(
  () => hasComparisonPeriod,
  (newHasComparisonPeriod) => {
    if (!newHasComparisonPeriod && comparisonPeriod.value.comparisonMode === 'custom') {
      updatePeriod(selectedPeriod.value, { comparisonMode: 'previousYear' });
    }
  },
  { immediate: true }
);

function openPeriodPopoup() {
  showPeriodSelectorPopUp.value = true;
}
function closePeriodPopoup() {
  showPeriodSelectorPopUp.value = false;
}

const handleSelectPeriod = (
  selectedPeriod: DatePeriodIntervalType | NielsenPeriodIntervalType,
  comparisonPeriod?: ComparisonPeriodType
) => {
  updatePeriod(selectedPeriod, comparisonPeriod);
  closePeriodPopoup();
};
</script>

<template>
  <div class="flex items-center gap-2">
    <ButtonBase icon-left="calendar" variant="secondary" :rounded="true" @click="openPeriodPopoup">
      <span class="space-x-1 text-left">
        <span v-if="intervalPeriodLabels.end" class="text-default">
          {{ t('commonLabel.from') }}
        </span>
        <span>
          {{ intervalPeriodLabels.start }}
        </span>
        <template v-if="intervalPeriodLabels.end">
          <span class="text-default"> {{ t('commonLabel.to') }} </span>
          <span>
            {{ intervalPeriodLabels.end }}
          </span>
        </template>
        <span v-if="comparisonPeriod.comparisonMode === 'previousYear'" class="text-default">
          {{ t('timeSelector.vsLastYear') }}
        </span>
      </span>
    </ButtonBase>

    <ButtonBase
      v-if="hasComparisonPeriod && comparisonPeriod.comparisonMode === 'custom' && comparisonPeriodLabels"
      icon-left="calendar"
      variant="secondary"
      :rounded="true"
      @click="openPeriodPopoup"
    >
      <span class="space-x-1 text-left">
        <span class="text-default"> {{ t('timeSelector.comparisonPeriod') }} </span>
        <span> {{ comparisonPeriodLabels.start }} </span>
        <span class="text-default">-</span>
        <span> {{ comparisonPeriodLabels.end }} </span>
      </span>
    </ButtonBase>
  </div>

  <KeepAlive>
    <PeriodPopup
      :show="showPeriodSelectorPopUp"
      :taxonomy="taxonomy"
      :selected-period="selectedPeriod"
      :comparison-period="comparisonPeriod"
      :has-comparison-period="hasComparisonPeriod"
      :referential-intervals="referentialIntervals"
      :carrefour-referential-intervals="carrefourReferentialIntervals"
      :available-periods="availablePeriods"
      :is-default-values="isDefaultPerimeter()"
      @on-select-period="handleSelectPeriod"
      @on-close="showPeriodSelectorPopUp = false"
    />
  </KeepAlive>
</template>
